import { z } from 'zod';

/**
 * Schema for all client-side environment variables.
 * These are injected by Vite via import.meta.env (must be prefixed with VITE_).
 * Extend this in your app by merging schemas.
 */
export const clientEnvSchema = z.object({
  // Convex
  VITE_CONVEX_URL: z.string().url('VITE_CONVEX_URL must be a valid URL'),

  // Sentry (optional — leave empty to disable)
  VITE_SENTRY_DSN: z.string().optional(),

  // App metadata
  VITE_APP_NAME: z.string().default('App'),
  VITE_APP_URL: z.string().url().optional(),

  // Injected by Vite
  MODE: z.enum(['development', 'production', 'test']).default('development'),
  DEV: z.boolean().default(true),
  PROD: z.boolean().default(false),
});

export type ClientEnv = z.infer<typeof clientEnvSchema>;

/**
 * Validates and returns typed client environment variables.
 * Call this once at app startup.
 *
 * @example
 * // apps/web/src/env.ts
 * import { validateClientEnv } from '@repo/env';
 * export const env = validateClientEnv(import.meta.env);
 */
export function validateClientEnv(rawEnv: Record<string, unknown>): ClientEnv {
  const result = clientEnvSchema.safeParse(rawEnv);

  if (!result.success) {
    const errors = result.error.flatten().fieldErrors;
    const messages = Object.entries(errors)
      .map(([key, msgs]) => `  ${key}: ${msgs?.join(', ')}`)
      .join('\n');

    throw new Error(`❌ Invalid environment variables:\n${messages}`);
  }

  return result.data;
}
